/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useParams, Link } from 'react-router-dom';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { useCreateInitiativeAppIntegrationMutation, useGetInitiativeAppIntegrationQuery } from '@api/initiative-app-integrations';
import { ProviderView } from '@features/integrations/IntegrationView';
import { CreateSetupFn, canShowDataTransfer, canShowEntityMapping } from '@g17eco/types/app-integration';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { CTAdminBreadcrumbs } from '../../components/breadcrumbs/CTAdminBreadcrumbs';
import { ROUTES } from '@constants/routes';
import { generateUrl } from '@routes/util';

export const AppIntegrationViewRoute = () => {

  const { code, initiativeId } = useParams<{ code: string, initiativeId: string }>()
  const isUserStaff = useAppSelector(isStaff);

  const query = useGetInitiativeAppIntegrationQuery({ initiativeId, code, }, { skip: !code });

  const [createMutation] = useCreateInitiativeAppIntegrationMutation()

  const onCreate: CreateSetupFn = (data) => {
    return createMutation({
      code,
      initiativeId,
      generatedAnswers: data.generatedAnswers,
    }).unwrap();
  }

  return (
    <Dashboard>
      <div className='pl-2 pb-4'>
        <CTAdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[
            { label: 'Connections', url: generateUrl(ROUTES.APP_INTEGRATIONS, { initiativeId }) },
            { label: 'Set up' },
          ]}
        />
      </div>
      <DashboardSectionTitle title={'Connection set up'} />
      <DashboardSection>
        <QueryWrapper query={query} onSuccess={(data) => (
          <>
            <ProviderView initiativeId={initiativeId} onCreate={onCreate} {...data} />
            {isUserStaff && (
              <div className='mt-3 d-flex gap-2'>
                {canShowEntityMapping(data.provider, data.status) && (
                  <Link
                    to={generateUrl(ROUTES.APP_INTEGRATIONS_ENTITY_MAPPING, { initiativeId, code })}
                    className='btn btn-outline-primary'
                  >
                    Manage Entity Mapping <FeatureStability stability='internal' />
                  </Link>
                )}
                {canShowDataTransfer(data.provider, data.status) && (
                  <Link
                    to={generateUrl(ROUTES.APP_INTEGRATIONS_DATA_TRANSFER, { initiativeId, code })}
                    className='btn btn-outline-primary'
                  >
                    Data Transfer Review <FeatureStability stability='internal' />
                  </Link>
                )}
              </div>
            )}
          </>
        )} />
      </DashboardSection>
    </Dashboard>
  );
}
